import { Column, Entity, JoinColumn, ManyToOne, PrimaryGeneratedColumn, Repository, Index, ManyToMany, JoinTable } from 'typeorm';
import { DataSourceConnectorType, DataSourceContext, ItemDef, QueryParams, SortDef } from './models';
import { Business, PublicBusiness } from '../business/business.entity';
import { PublicSite, Site } from '../site/site.entity';
import { FilterDef } from './models/filter.model';
import { DataStore } from './data-store/data-store.entity';
import { Crypt } from '../_core/crypt';
import { IsOptional } from 'class-validator';
import { LocaleDto } from '../creative-unit-package/models/locale.dto';
import { DataSourceConnectorConfig } from './data-source-config.types';
import { ConnectorConfigAssetCollection } from './connectors/asset-collection/models';
import { ConnectorConfigBox } from './connectors/box/models';
import { ConnectorConfigBrandshare } from './connectors/brandshare/models';
import { ConnectorConfigBria } from './connectors/bria/models';
import { ConnectorConfigCloudinary } from './connectors/cloudinary/models';
import { ConnectorConfigCSV } from './connectors/csv/models';
import { ConnectorConfigNative } from './connectors/native/models';
import { ConnectorConfigPexels } from './connectors/pexels/models';
import { ConnectorConfigSmartSheet } from './connectors/smartsheet/models';
import { ConnectorConfigStrapi } from './connectors/strapi/models';
import { normalizeCloudinaryConnectorConfig } from './connectors/cloudinary/cloudinary-credentials.util';
import { sanitizeDataSourceConfigForPublic, unpackDataSourceConfigExtensions } from './data-source-config-envelope';

export type PublicDataSourceConfig = {
	[key: string]: unknown;
	target: string;
	type?: DataSourceConnectorType;
	context?: DataSourceContext[];
	mapping?: ItemDef[];
	params?: QueryParams;
	columnSeparator?: string;
	environment?: string;
	sortableFields?: SortDef[];
	filters?: FilterDef[];
	previewPath?: string;
	assetPathProperty?: string;
	assetTitleProperty?: string;
	tagPath?: string;
	hasAccessToken?: boolean;
	hasApiKey?: boolean;
	hasApiSecret?: boolean;
	hasClientSecret?: boolean;
	hasDeveloperToken?: boolean;
	hasRefreshToken?: boolean;
	hasJwtPrivateKey?: boolean;
	hasJwtPrivateKeyPassphrase?: boolean;
	attribution?: string;
	saveToLibrary?: boolean;
	baseUrl?: string;
	clientId?: string;
	enterpriseId?: string;
	timeout?: number;
	includeSubfolders?: boolean;
	pageSize?: number;
	allowedFileTypes?: string[];
	authMethod?: string;
	includeSharedLinks?: boolean;
	jwtKeyId?: string;
	linkExpireInDays?: number;
	loadFullFolderTree?: boolean;
	useBriaIframe?: boolean;
	models?: Array<{ name: string; value: string }>;
	supportedInputs?: string[];
	aspectRatios?: string[];
	rightsFieldMap?: import('./models/rights.model').RightsFieldMap;
};

export type PublicDataSource = Pick<
	DataSource,
	'id' | 'name' | 'slug' | 'connectorType' | 'dataSourceLink' | 'siteId' | 'locale' | 'created' | 'modified' | 'public'
> & {
	site?: PublicSite;
	businesses?: PublicBusiness[] | Business[] | Array<Partial<Business>>;
	config?: PublicDataSourceConfig;
};

@Entity('dataSources')
@Index(['slug', 'siteId'], { unique: true })
export class DataSource {
	public dataStoreRepository?: Repository<DataStore>;

	constructor(value?: Partial<DataSource>, dataStoreRepository?: Repository<DataStore>) {
		// Copy properties from value
		for (const k in value) {
			try {
				const val = value[k];
				if (val !== null && val !== undefined) {
					this[k] = val;
				}
			} catch (error) {
				console.error(`Error assigning property ${k}:`, error, 'Value type:', typeof value[k]);
				// Continue with other properties even if one fails
			}
		}
		this.dataStoreRepository = dataStoreRepository;

		// Safely handle config access with error handling
		try {
			if (this.config && !this.config.target?.length && this.connectorType === DataSourceConnectorType.Native) {
				// Create a new config object to avoid modifying the original
				const updatedConfig = { ...this.config, target: this.id };
				this.config = updatedConfig;
			}
		} catch (error) {
			console.error('Error accessing config in DataSource constructor:', error);
			// If config access fails, just continue without setting the target
		}
	}

	@PrimaryGeneratedColumn('uuid')
	id: string;

	@Column('text', { nullable: false })
	name: string;

	@Column({ type: 'text', nullable: false })
	slug: string;

	@Column({ type: 'enum', enum: DataSourceConnectorType })
	connectorType: DataSourceConnectorType;

	@Column('text', { nullable: true })
	dataSourceLink: string;

	@Column('uuid', { nullable: false })
	siteId: string;
	@ManyToOne(() => Site, {
		onDelete: 'CASCADE',
		nullable: false
	})
	@JoinColumn({ name: 'siteId' })
	site: Site;

	@ManyToMany(() => Business, {
		nullable: true
	})
	@JoinTable({
		name: 'dataSourceBusinesses',
		joinColumn: {
			name: 'dataSourcesId',
			referencedColumnName: 'id'
		},
		inverseJoinColumn: {
			name: 'businessesId',
			referencedColumnName: 'id'
		}
	})
	businesses?: Business[] | Array<Partial<Business>>;

	@Column('text', {
		name: 'config',
		nullable: true
	})
	_config: string;
	public get config(): DataSourceConnectorConfig {
		// TOOD: Create a migration to encrypt/decrypt based on siteId.
		// All existing data sources should be decrypted and re-encrypted with the siteId.
		let decrypted = Crypt.decryptObject<DataSourceConnectorConfig>(this._config, undefined);
		if (decrypted && this.connectorType === DataSourceConnectorType.Cloudinary) {
			decrypted = normalizeCloudinaryConnectorConfig(decrypted);
		}
		// Target to this source's id if empty it's a native source.
		if (decrypted && !decrypted.target?.length && this.connectorType === DataSourceConnectorType.Native) {
			decrypted.target = this.id;
		}
		return decrypted;
	}
	public set config(value: DataSourceConnectorConfig) {
		// TOOD: Create a migration to encrypt/decrypt based on siteId.
		// All existing data sources should be decrypted and re-encrypted with the siteId.
		const unpacked = unpackDataSourceConfigExtensions(value);
		this._config = Crypt.encryptObject(
			this.connectorType === DataSourceConnectorType.Cloudinary ? normalizeCloudinaryConnectorConfig(unpacked) : unpacked,
			undefined
		);
	}

	@IsOptional()
	@Column('jsonb', { nullable: true })
	locale?: LocaleDto;

	@Column('timestamptz', { nullable: true, default: () => 'NOW()' })
	created?: string;

	@Column('timestamptz', { nullable: true, default: () => 'NOW()' })
	modified?: string;

	@Column('boolean', { default: false })
	public: boolean = false;

	public toPublic(skipMask: boolean = false) {
		const pub: Partial<PublicDataSource> = {
			id: this.id,
			name: this.name,
			slug: this.slug,
			dataSourceLink: this.dataSourceLink,
			connectorType: this.connectorType,
			siteId: this.siteId,
			businesses: this.businesses,
			locale: this.locale,
			created: this.created,
			modified: this.modified,
			public: this.public
		};

		const connectorTypeConfig = getDataSourceConnectorType(this.connectorType);

		if (this.site) {
			pub.site = new Site(this.site).toPublic();
		}

		if (this.businesses?.length) {
			pub.businesses = this.businesses.map(b => {
				return new Business(b).toPublic();
			});
		}

		const storedConf = this.config;
		const conf = storedConf ? sanitizeDataSourceConfigForPublic(storedConf) : storedConf;
		if (conf) {
			pub.config = { ...((conf as unknown) as Record<string, unknown>), target: conf.target };

			if (conf.context) {
				pub.config.context = conf.context;
			}

			if (conf.type !== undefined) {
				pub.config.type = conf.type;
			}

			if (conf.mapping) {
				pub.config.mapping = conf.mapping;
			}

			if (conf.params) {
				pub.config.params = conf.params;
			}

			if (conf.sortableFields) {
				pub.config.sortableFields = conf.sortableFields;
			}

			const filters = conf.filters ?? connectorTypeConfig?.filters;
			if (filters) {
				pub.config.filters = filters;
			}

			if (conf.previewPath) {
				pub.config.previewPath = conf.previewPath;
			}

			if (conf.assetPathProperty) {
				pub.config.assetPathProperty = conf.assetPathProperty;
			}

			if (conf.assetTitleProperty) {
				pub.config.assetTitleProperty = conf.assetTitleProperty;
			}

			if (conf.tagPath) {
				pub.config.tagPath = conf.tagPath;
			}

			const attribution = conf.attribution ?? connectorTypeConfig?.attribution;
			if (attribution !== undefined) {
				pub.config.attribution = attribution;
			}

			if ('columnSeparator' in conf && conf.columnSeparator !== undefined) {
				pub.config.columnSeparator = conf.columnSeparator;
			}
			if ('environment' in conf && conf.environment !== undefined) {
				pub.config.environment = conf.environment;
			}
			if (conf.clientId !== undefined) {
				pub.config.clientId = conf.clientId;
			}
			if (conf.enterpriseId !== undefined) {
				pub.config.enterpriseId = conf.enterpriseId;
			}

			if (conf.saveToLibrary !== undefined) {
				pub.config.saveToLibrary = conf.saveToLibrary;
			}

			if (conf.baseUrl !== undefined) {
				pub.config.baseUrl = conf.baseUrl;
			}

			if ('timeout' in conf && conf.timeout !== undefined) {
				pub.config.timeout = conf.timeout;
			}
			if ('includeSubfolders' in conf && conf.includeSubfolders !== undefined) {
				pub.config.includeSubfolders = conf.includeSubfolders;
			}
			if ('pageSize' in conf && conf.pageSize !== undefined) {
				pub.config.pageSize = conf.pageSize;
			}
			if ('allowedFileTypes' in conf && conf.allowedFileTypes !== undefined) {
				pub.config.allowedFileTypes = conf.allowedFileTypes;
			}
			if ('authMethod' in conf && conf.authMethod !== undefined) {
				pub.config.authMethod = conf.authMethod;
			}
			if ('includeSharedLinks' in conf && conf.includeSharedLinks !== undefined) {
				pub.config.includeSharedLinks = conf.includeSharedLinks;
			}
			if ('jwtKeyId' in conf && conf.jwtKeyId !== undefined) {
				pub.config.jwtKeyId = conf.jwtKeyId;
			}
			if ('linkExpireInDays' in conf && conf.linkExpireInDays !== undefined) {
				pub.config.linkExpireInDays = conf.linkExpireInDays;
			}
			if ('loadFullFolderTree' in conf && conf.loadFullFolderTree !== undefined) {
				pub.config.loadFullFolderTree = conf.loadFullFolderTree;
			}
			if ('useBriaIframe' in conf && conf.useBriaIframe !== undefined) {
				pub.config.useBriaIframe = conf.useBriaIframe;
			}
			if ('models' in conf && conf.models !== undefined) {
				pub.config.models = conf.models;
			}
			if ('supportedInputs' in conf && conf.supportedInputs !== undefined) {
				pub.config.supportedInputs = conf.supportedInputs;
			}
			if ('aspectRatios' in conf && conf.aspectRatios !== undefined) {
				pub.config.aspectRatios = conf.aspectRatios;
			}

			// rightsFieldMap (U1): per-data-source mapping that surfaces DAM rights
			// signals on each item. Surfaced through toPublic so the admin dialog
			// can hydrate the saved configuration on reopen. Contains no credentials —
			// only dot-notation field paths and a per-status valueMap of canonical
			// enum labels. Access to this endpoint is already business-scoped, which
			// is the same protection boundary as `mapping`, `previewPath`, etc.
			if (conf.rightsFieldMap) {
				pub.config.rightsFieldMap = conf.rightsFieldMap;
			}

			// Credentials never leave the API; these flags let the UI distinguish
			// "credential stored" from "none" without exposing the value.
			if (!skipMask) {
				if (storedConf.accessToken) {
					pub.config.hasAccessToken = true;
				}
				if ('apiKey' in storedConf && storedConf.apiKey) {
					pub.config.hasApiKey = true;
				}
				if ('apiSecret' in storedConf && storedConf.apiSecret) {
					pub.config.hasApiSecret = true;
				}
				if ('clientSecret' in storedConf && storedConf.clientSecret) {
					pub.config.hasClientSecret = true;
				}
				if ('developerToken' in storedConf && storedConf.developerToken) {
					pub.config.hasDeveloperToken = true;
				}
				if ('refreshToken' in storedConf && storedConf.refreshToken) {
					pub.config.hasRefreshToken = true;
				}
				if ('jwtPrivateKey' in storedConf && storedConf.jwtPrivateKey) {
					pub.config.hasJwtPrivateKey = true;
				}
				if ('jwtPrivateKeyPassphrase' in storedConf && storedConf.jwtPrivateKeyPassphrase) {
					pub.config.hasJwtPrivateKeyPassphrase = true;
				}
			}
		}

		return pub as PublicDataSource;
	}

	public toExport(): Omit<PublicDataSource, 'id' | 'siteId'> {
		const item = this.toPublic(true);
		item.id = undefined;
		item.siteId = undefined;

		return item;
	}
}

export function getDataSourceConnectorType(value: DataSourceConnectorType): DataSourceConnectorConfig {
	switch (value) {
		case DataSourceConnectorType.AssetCollection:
			return new ConnectorConfigAssetCollection();
		case DataSourceConnectorType.Box:
			return new ConnectorConfigBox();
		case DataSourceConnectorType.Brandshare:
			return new ConnectorConfigBrandshare();
		case DataSourceConnectorType.Bria:
			return new ConnectorConfigBria();
		case DataSourceConnectorType.Cloudinary:
			return new ConnectorConfigCloudinary();
		case DataSourceConnectorType.CSV:
			return new ConnectorConfigCSV();
		case DataSourceConnectorType.Native:
			return new ConnectorConfigNative();
		case DataSourceConnectorType.Pexels:
			return new ConnectorConfigPexels();
		case DataSourceConnectorType.SmartSheet:
			return new ConnectorConfigSmartSheet();
		case DataSourceConnectorType.Strapi:
			return new ConnectorConfigStrapi();
	}
}
